import React, { useState, useEffect, useCallback, useRef } from 'react';
import { productsAPI } from '../services/api';
import { toast } from 'react-toastify';
import useScrollToTop from '../hooks/useScrollToTop';
import { 
  Filter, 
  Search, 
  Grid, 
  List, 
  Package,
  TrendingUp,
  Award,
  ChevronLeft,
  ChevronRight,
  X,
  SlidersHorizontal,
  ArrowUpDown
} from 'lucide-react';

const Products = () => {
  // Configuration
  const ITEMS_PER_PAGE = 12;
  
  const [allProducts, setAllProducts] = useState([]); // Store all products for client-side filtering
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    minPrice: '',
    maxPrice: '',
    search: '',
    sort: 'newest'
  });
  const [inputValues, setInputValues] = useState({
    search: '',
    minPrice: '',
    maxPrice: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });

  const scrollToTop = useScrollToTop();
  const debounceTimerRef = useRef(null);

  const categories = [
    { value: '', label: 'All Categories', icon: Package },
    { value: 'door-frames', label: 'Door Frames', icon: Package },
    { value: 'window-frames', label: 'Window Frames', icon: Package },
    { value: 'ventilations', label: 'Ventilations', icon: Package },
    { value: 'accessories', label: 'Accessories', icon: Package },
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First', icon: TrendingUp },
    { value: 'price-low', label: 'Price: Low to High', icon: ArrowUpDown },
    { value: 'price-high', label: 'Price: High to Low', icon: ArrowUpDown },
    { value: 'name', label: 'Name: A to Z', icon: ArrowUpDown },
    { value: 'popular', label: 'Most Popular', icon: Award },
  ];

  // Load all products initially
  useEffect(() => {
    fetchAllProducts();
  }, []);

  // Handle real-time search and other filters
  useEffect(() => {
    if (allProducts.length > 0) {
      filterProducts();
    }
  }, [allProducts, filters, inputValues.search, pagination.currentPage]);

  // Handle price range changes with debounce (for API efficiency)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (allProducts.length > 0) {
        filterProducts();
      }
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters.minPrice, filters.maxPrice]);

  const fetchAllProducts = async () => {
    setLoading(true);
    try {
      const response = await productsAPI.getAll({ limit: 1000 }); // Get all products
      console.log('Products API response:', response);
      
      // Handle the response structure correctly
      let productsData = [];
      if (response && response.data) {
        if (response.data.success && Array.isArray(response.data.data)) {
          productsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          productsData = response.data;
        }
      }
      
      console.log('Processed products data:', productsData);
      
      setAllProducts(productsData);
      setProducts(productsData);
      setPagination({
        currentPage: 1,
        totalPages: Math.ceil(productsData.length / ITEMS_PER_PAGE),
        totalItems: productsData.length
      });
    } catch (error) {
      console.error('Error fetching products:', error);
      // Set empty arrays as fallback
      setAllProducts([]);
      setProducts([]);
      setPagination({
        currentPage: 1,
        totalPages: 0,
        totalItems: 0
      });
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering for real-time search
  const filterProducts = useCallback(() => {
    setSearching(true);
    
    // Use setTimeout to show loading state briefly for UX feedback
    setTimeout(() => {
      // Defensive check: ensure allProducts is an array
      if (!Array.isArray(allProducts) || allProducts.length === 0) {
        console.warn('allProducts is not an array or is empty:', allProducts);
        setProducts([]);
        setPagination(prev => ({
          ...prev,
          totalPages: 0,
          totalItems: 0
        }));
        setSearching(false);
        return;
      }

      let filtered = [...allProducts];

      // Real-time search filter (substring matching)
      const searchTerm = inputValues.search.toLowerCase().trim();
      if (searchTerm) {
        filtered = filtered.filter(product => 
          (product && product.name && product.name.toLowerCase().includes(searchTerm)) ||
          (product && product.description && product.description.toLowerCase().includes(searchTerm)) ||
          (product && product.category && product.category.toLowerCase().includes(searchTerm))
        );
      }

      // Category filter
      if (filters.category) {
        filtered = filtered.filter(product => product && product.category === filters.category);
      }

      // Price range filter
      if (filters.minPrice) {
        const minPrice = parseFloat(filters.minPrice);
        if (!isNaN(minPrice)) {
          filtered = filtered.filter(product => product.price >= minPrice);
        }
      }

      if (filters.maxPrice) {
        const maxPrice = parseFloat(filters.maxPrice);
        if (!isNaN(maxPrice)) {
          filtered = filtered.filter(product => product.price <= maxPrice);
        }
      }

      // Sorting
      switch (filters.sort) {
        case 'price-low':
          filtered.sort((a, b) => a.price - b.price);
          break;
        case 'price-high':
          filtered.sort((a, b) => b.price - a.price);
          break;
        case 'name':
          filtered.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'popular':
          filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          break;
        case 'newest':
        default:
          filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          break;
      }

      // Pagination - More items per page for better desktop experience  
      const itemsPerPage = ITEMS_PER_PAGE;
      const startIndex = (pagination.currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      
      // Ensure filtered is an array before slicing
      if (!Array.isArray(filtered)) {
        console.warn('filterProducts: filtered is not an array, using empty array');
        filtered = [];
      }
      
      const paginatedProducts = filtered.slice(startIndex, endIndex);

      setProducts(paginatedProducts);
      setPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(filtered.length / itemsPerPage),
        totalItems: filtered.length
      }));
      
      setSearching(false);
    }, 50); // Very brief delay for smooth UX
  }, [allProducts, filters, inputValues.search, pagination.currentPage]); // useCallback dependencies

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    scrollToTop();
  };

  // Real-time search handler - updates immediately
  const handleSearchChange = (value) => {
    setInputValues(prev => ({
      ...prev,
      search: value
    }));
    // No debounce for search - instant filtering
  };

  // Price input handler - with debounce for performance
  const handlePriceChange = (key, value) => {
    setInputValues(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Update filters after a delay for price inputs
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500);
  };

  const renderProductCard = (product, index) => (
    <div 
      key={product._id} 
      className={`group bg-white/25 dark:bg-gray-800/40 backdrop-blur-lg border border-white/20 dark:border-gray-700/30 rounded-2xl overflow-hidden hover:border-white/40 dark:hover:border-gray-600/50 hover:shadow-2xl dark:hover:shadow-gray-900/20 transition-all duration-500 ${
        viewMode === 'list' ? 'flex' : ''
      }`}
    >
      <div className={`${viewMode === 'list' ? 'w-64 flex-shrink-0' : 'aspect-w-16 aspect-h-12'} overflow-hidden relative`}>
        <img
          src={
            product.images?.[0]?.url 
              ? (product.images[0].url.startsWith('http') 
                  ? product.images[0].url 
                  : `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${product.images[0].url}`)
              : '/images/products/1.png'
          }
          alt={product.images?.[0]?.alt || product.name}
          className={`object-cover transition-transform duration-700 group-hover:scale-105 ${
            viewMode === 'list' ? 'w-full h-full' : 'w-full h-56'
          }`}
          onError={(e) => {
            e.target.src = '/images/products/1.png';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 dark:from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute top-4 left-4">
          <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-800 dark:text-gray-200 text-xs font-bold px-3 py-1.5 rounded-full border border-white/40 dark:border-gray-600/40 transition-colors duration-300">
            {product.category.replace('-', ' ').toUpperCase()}
          </span>
        </div>
        {product.featured && (
          <div className="absolute top-4 right-4">
            <Award className="h-5 w-5 text-yellow-500" />
          </div>
        )}
      </div>
      
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center justify-end mb-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 font-medium transition-colors duration-300">
            {product.specifications?.modelNumber ? `Model: ${product.specifications.modelNumber}` : `SKU: ${product._id.slice(-6).toUpperCase()}`}
          </div>
        </div>
        
        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors line-clamp-2">
          {product.name}
        </h3>
        
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2 leading-relaxed flex-grow transition-colors duration-300">
          {product.description}
        </p>
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex flex-col">
            <span className="text-2xl font-bold text-gray-900 dark:text-white transition-colors duration-300">
              ₹{product.price?.toLocaleString() || '0'}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-sm text-gray-500 dark:text-gray-400 line-through transition-colors duration-300">
                ₹{product.originalPrice.toLocaleString()}
              </span>
            )}
          </div>
          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-2 py-1 rounded-full font-medium transition-colors duration-300">
            Available
          </span>
        </div>
      </div>
    </div>
  );

  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center parallax-bg">
        <div className="liquid-glass-card p-8 rounded-3xl border border-white/40 premium-glow">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 liquid-text font-bold text-center text-gray-900 dark:text-white transition-colors duration-300">Loading Products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-blue-900/30 dark:to-purple-900/30 transition-colors duration-300">
      <div className="container-custom header-spacer pb-6 max-w-8xl">
        <div className="flex flex-col xl:flex-row gap-6">
          {/* Mobile Filter Overlay */}
          {showFilters && (
            <div className="xl:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-end">
              <div className="w-full bg-white dark:bg-gray-800 rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up transition-colors duration-300">
                {/* Mobile Filter Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200/50 dark:border-gray-700/50 flex-shrink-0 transition-colors duration-300">
                  <h3 className="font-bold text-gray-900 dark:text-white flex items-center text-lg transition-colors duration-300">
                    <Filter className="h-5 w-5 mr-3 text-purple-600 dark:text-purple-400" />
                    Filters
                  </h3>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-300"
                  >
                    <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
                
                {/* Mobile Scrollable Filter Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
                  {/* Mobile Search */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                      Search Products {inputValues.search && (
                        <span className="text-xs text-purple-600 dark:text-purple-400 font-medium ml-1 transition-colors duration-300">
                          ({pagination.totalItems} results)
                        </span>
                      )}
                    </label>
                    <div className="relative">
                      <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 transition-colors ${
                        searching ? 'text-purple-500 animate-pulse' : 'text-gray-400 dark:text-gray-500'
                      }`} />
                      <input
                        type="text"
                        placeholder="Search by name or description..."
                        className={`w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                          inputValues.search ? 'border-purple-300 dark:border-purple-600' : 'border-gray-200 dark:border-gray-600'
                        }`}
                        value={inputValues.search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                      />
                      {inputValues.search && (
                        <button
                          onClick={() => {
                            setInputValues(prev => ({ ...prev, search: '' }));
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mobile Category */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                      Category
                    </label>
                    <div className="space-y-2">
                      {categories.map(cat => {
                        const IconComponent = cat.icon;
                        return (
                          <label key={cat.value} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 transition-colors duration-300 cursor-pointer">
                            <input
                              type="radio"
                              name="category"
                              value={cat.value}
                              checked={filters.category === cat.value}
                              onChange={(e) => handleFilterChange('category', e.target.value)}
                              className="sr-only"
                            />
                            <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                              filters.category === cat.value 
                                ? 'border-purple-500 bg-purple-500' 
                                : 'border-gray-300 dark:border-gray-500'
                            }`}>
                              {filters.category === cat.value && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                            <IconComponent className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2 flex-shrink-0 transition-colors duration-300" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">{cat.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Mobile Price Range */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                      Price Range (₹)
                    </label>
                    <div className="space-y-3">
                      <div className="flex space-x-2">
                        <div className="flex-1 min-w-0">
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 transition-colors duration-300">Minimum</label>
                          <input
                            type="number"
                            placeholder="0"
                            className="w-full px-3 py-2.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                            value={inputValues.minPrice}
                            onChange={(e) => handlePriceChange('minPrice', e.target.value)}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 transition-colors duration-300">Maximum</label>
                          <input
                            type="number"
                            placeholder="∞"
                            className="w-full px-3 py-2.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                            value={inputValues.maxPrice}
                            onChange={(e) => handlePriceChange('maxPrice', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {['1000', '5000', '10000', '25000'].map(price => (
                          <button
                            key={price}
                            onClick={() => {
                              handleFilterChange('maxPrice', price);
                              setInputValues(prev => ({ ...prev, maxPrice: price }));
                            }}
                            className="text-xs bg-gray-100 dark:bg-gray-600 hover:bg-purple-100 dark:hover:bg-purple-800 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg transition-colors duration-300 flex-shrink-0"
                          >
                            ₹{price}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Sort */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                      Sort By
                    </label>
                    <div className="space-y-2">
                      {sortOptions.map(option => {
                        const IconComponent = option.icon;
                        return (
                          <label key={option.value} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 transition-colors duration-300 cursor-pointer">
                            <input
                              type="radio"
                              name="sort"
                              value={option.value}
                              checked={filters.sort === option.value}
                              onChange={(e) => handleFilterChange('sort', e.target.value)}
                              className="sr-only"
                            />
                            <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                              filters.sort === option.value 
                                ? 'border-purple-500 bg-purple-500' 
                                : 'border-gray-300 dark:border-gray-500'
                            }`}>
                              {filters.sort === option.value && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                            <IconComponent className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2 flex-shrink-0 transition-colors duration-300" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Mobile Filter Actions */}
                <div className="border-t border-gray-200/50 dark:border-gray-700/50 p-4 flex-shrink-0 space-y-2 transition-colors duration-300">
                  <button
                    onClick={() => {
                      const resetValues = {
                        category: '',
                        minPrice: '',
                        maxPrice: '',
                        search: '',
                        sort: 'newest'
                      };
                      setFilters(resetValues);
                      setInputValues({
                        search: '',
                        minPrice: '',
                        maxPrice: ''
                      });
                      setPagination(prev => ({ ...prev, currentPage: 1 }));
                    }}
                    className="w-full bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-semibold transition-colors duration-300 text-sm"
                  >
                    Clear All Filters
                  </button>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="w-full bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 text-white py-3 rounded-xl font-semibold transition-colors duration-300 text-sm"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Desktop Enhanced Filters Sidebar */}
          <div className="xl:w-72 flex-shrink-0 hidden xl:block">
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-700/30 shadow-xl sticky top-24 max-h-[calc(100vh-7rem)] overflow-hidden flex flex-col transition-colors duration-300">
              {/* Desktop Filter Header - Fixed */}
              <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200/50 dark:border-gray-700/50 flex-shrink-0 transition-colors duration-300">
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center text-xl transition-colors duration-300">
                  <Filter className="h-5 w-5 mr-3 text-purple-600 dark:text-purple-400" />
                  Filters
                </h3>
              </div>
              
              {/* Desktop Scrollable Filter Content */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-6 pt-4 space-y-6">
              
              {/* Search */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 md:mb-3 transition-colors duration-300">
                  Search Products {inputValues.search && (
                    <span className="text-xs text-purple-600 dark:text-purple-400 font-medium ml-1 transition-colors duration-300">
                      ({pagination.totalItems} results)
                    </span>
                  )}
                </label>
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 transition-colors ${
                    searching ? 'text-purple-500 animate-pulse' : 'text-gray-400 dark:text-gray-500'
                  }`} />
                  <input
                    type="text"
                    placeholder="Search by name or description..."
                    className={`w-full pl-10 pr-4 py-2.5 md:py-3 bg-white/80 dark:bg-gray-700/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                      inputValues.search ? 'border-purple-300 dark:border-purple-600' : 'border-gray-200 dark:border-gray-600'
                    }`}
                    value={inputValues.search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                  {inputValues.search && (
                    <button
                      onClick={() => {
                        setInputValues(prev => ({ ...prev, search: '' }));
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 md:mb-3 transition-colors duration-300">
                  Category
                </label>
                <div className="space-y-1.5 md:space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                  {categories.map(cat => {
                    const IconComponent = cat.icon;
                    return (
                      <label key={cat.value} className="flex items-center p-2.5 md:p-3 bg-white/60 dark:bg-gray-700/60 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 transition-colors duration-300 cursor-pointer">
                        <input
                          type="radio"
                          name="category"
                          value={cat.value}
                          checked={filters.category === cat.value}
                          onChange={(e) => handleFilterChange('category', e.target.value)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-2.5 md:mr-3 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                          filters.category === cat.value 
                            ? 'border-purple-500 bg-purple-500' 
                            : 'border-gray-300 dark:border-gray-500'
                        }`}>
                          {filters.category === cat.value && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <IconComponent className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2 flex-shrink-0 transition-colors duration-300" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate transition-colors duration-300">{cat.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 md:mb-3 transition-colors duration-300">
                  Price Range (₹)
                </label>
                <div className="space-y-3">
                  <div className="flex space-x-2 md:space-x-3">
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 transition-colors duration-300">Minimum</label>
                      <input
                        type="number"
                        placeholder="0"
                        className="w-full px-2.5 md:px-3 py-2 md:py-2.5 bg-white/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        value={inputValues.minPrice}
                        onChange={(e) => handlePriceChange('minPrice', e.target.value)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 transition-colors duration-300">Maximum</label>
                      <input
                        type="number"
                        placeholder="∞"
                        className="w-full px-2.5 md:px-3 py-2 md:py-2.5 bg-white/80 dark:bg-gray-700/80 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        value={inputValues.maxPrice}
                        onChange={(e) => handlePriceChange('maxPrice', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 md:gap-2">
                    {['1000', '5000', '10000', '25000'].map(price => (
                      <button
                        key={price}
                        onClick={() => {
                          handleFilterChange('maxPrice', price);
                          setInputValues(prev => ({ ...prev, maxPrice: price }));
                        }}
                        className="text-xs bg-gray-100 hover:bg-purple-100 text-gray-700 px-2.5 md:px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                      >
                        ₹{price}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3">
                  Sort By
                </label>
                <div className="space-y-1.5 md:space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                  {sortOptions.map(option => {
                    const IconComponent = option.icon;
                    return (
                      <label key={option.value} className="flex items-center p-2.5 md:p-3 bg-white/60 dark:bg-gray-700/60 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 transition-colors duration-300 cursor-pointer">
                        <input
                          type="radio"
                          name="sort"
                          value={option.value}
                          checked={filters.sort === option.value}
                          onChange={(e) => handleFilterChange('sort', e.target.value)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-2.5 md:mr-3 flex items-center justify-center flex-shrink-0 ${
                          filters.sort === option.value 
                            ? 'border-purple-500 bg-purple-500' 
                            : 'border-gray-300'
                        }`}>
                          {filters.sort === option.value && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <IconComponent className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-700 truncate">{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              </div>

              {/* Clear Filters Button - Fixed at bottom */}
              <div className="border-t border-gray-200/50 p-4 xl:p-6 pt-4 flex-shrink-0">
                <button
                  onClick={() => {
                    const resetValues = {
                      category: '',
                      minPrice: '',
                      maxPrice: '',
                      search: '',
                      sort: 'newest'
                    };
                    setFilters(resetValues);
                    setInputValues({
                      search: '',
                      minPrice: '',
                      maxPrice: ''
                    });
                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 md:py-3 rounded-xl font-semibold transition-colors text-sm"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>

          {/* Enhanced Products Grid */}
          <div className="flex-1">
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-700/30 shadow-xl transition-colors duration-300">
              {/* Products Header */}
              <div className="p-4 xl:p-6 border-b border-gray-200/50 dark:border-gray-700/50 transition-colors duration-300">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 xl:gap-4">
                  <div>
                    <h2 className="text-lg xl:text-xl font-bold text-gray-900 dark:text-white transition-colors duration-300">
                      {loading ? 'Loading...' : `${pagination.totalItems} Products Found`}
                    </h2>
                    {!loading && (
                      <p className="text-xs xl:text-sm text-gray-600 mt-1">
                        Page {pagination.currentPage} of {pagination.totalPages} • Premium steel solutions
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="xl:hidden liquid-glass-card border border-white/30 px-3 py-2 rounded-xl font-semibold text-gray-700 hover:border-purple-400 transition-all duration-300 flex items-center"
                    >
                      <SlidersHorizontal className="h-4 w-4 mr-2" />
                      Filters
                    </button>
                    <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 transition-colors duration-300">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 rounded-md transition-all duration-200 ${
                          viewMode === 'grid' 
                            ? 'bg-white dark:bg-gray-800 text-purple-600 shadow-sm' 
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                        }`}
                      >
                        <Grid className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 rounded-md transition-all duration-200 ${
                          viewMode === 'list' 
                            ? 'bg-white dark:bg-gray-800 text-purple-600 shadow-sm' 
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                        }`}
                      >
                        <List className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Display */}
              <div className="p-4 xl:p-6">
                {loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 lg:gap-6">
                    {[...Array(15)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="bg-gray-200 rounded-2xl h-56 mb-4"></div>
                        <div className="space-y-3">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                          <div className="h-8 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : products.length > 0 ? (
                  <div className={`${
                    viewMode === 'grid' 
                      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 lg:gap-6' 
                      : 'space-y-6'
                  }`}>
                    {products.map((product, index) => renderProductCard(product, index))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="max-w-md mx-auto">
                      <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">
                        {inputValues.search ? 'No search results found' : 'No products found'}
                      </h3>
                      <p className="text-gray-500 mb-6">
                        {inputValues.search 
                          ? `No products match "${inputValues.search}". Try different keywords or clear your search.`
                          : 'We couldn\'t find any products matching your criteria. Try adjusting your filters.'
                        }
                      </p>
                      <button
                        onClick={() => {
                          const resetValues = {
                            category: '',
                            minPrice: '',
                            maxPrice: '',
                            search: '',
                            sort: 'newest'
                          };
                          setFilters(resetValues);
                          setInputValues({
                            search: '',
                            minPrice: '',
                            maxPrice: ''
                          });
                          setPagination(prev => ({ ...prev, currentPage: 1 }));
                        }}
                        className="liquid-glass-button text-white px-6 py-2 rounded-xl font-semibold border border-white/40 hover:shadow-lg transition-all duration-300"
                      >
                        {inputValues.search ? 'Clear Search' : 'Clear All Filters'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Enhanced Pagination */}
              {pagination.totalPages > 1 && !loading && (
                <div className="p-6 border-t border-gray-200/50">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-600">
                      Showing {((pagination.currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(pagination.currentPage * ITEMS_PER_PAGE, pagination.totalItems)} of {pagination.totalItems} products
                    </div>
                    <nav className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
                          scrollToTop();
                        }}
                        disabled={pagination.currentPage === 1}
                        className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                          let pageNum;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => {
                                setPagination(prev => ({ ...prev, currentPage: pageNum }));
                                scrollToTop();
                              }}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                pagination.currentPage === pageNum
                                  ? 'bg-purple-600 text-white'
                                  : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => {
                          setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
                          scrollToTop();
                        }}
                        disabled={pagination.currentPage === pagination.totalPages}
                        className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </button>
                    </nav>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
